import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import Header from '../components/Header';
import BuiltBy from '../components/BuiltBy';
import { portfolioData } from '../data/portfolio';

const MyFootprintPage = () => {
  const { footprintStory } = portfolioData;
  const [activeYearIndex, setActiveYearIndex] = useState(0);
  const [activeMemoryIndex, setActiveMemoryIndex] = useState(0);
  const [hasScrolled, setHasScrolled] = useState(false);
  const timelineRef = useRef(null);
  const isScrollingRef = useRef(false);

  const currentYear = footprintStory[activeYearIndex];
  const currentMemory = currentYear.memories[activeMemoryIndex];
  const hasMultipleMemories = currentYear.memories.length > 1;

  // Scroll to specific year
  const scrollToYear = useCallback((index) => {
    if (timelineRef.current) {
      const itemHeight = 200;
      timelineRef.current.scrollTo({
        top: index * itemHeight,
        behavior: 'smooth'
      });
    }
    setActiveYearIndex(index);
    setActiveMemoryIndex(0);
  }, []);

  // Navigate memories
  const navigateMemory = useCallback((direction) => {
    if (direction === 'next' && activeMemoryIndex < currentYear.memories.length - 1) {
      setActiveMemoryIndex(prev => prev + 1);
    } else if (direction === 'prev' && activeMemoryIndex > 0) {
      setActiveMemoryIndex(prev => prev - 1);
    }
  }, [activeMemoryIndex, currentYear.memories.length]);

  // Handle global wheel event - scrollable from anywhere on the page
  useEffect(() => {
    const handleWheel = (e) => {
      e.preventDefault();
      
      // Debounce to prevent too rapid scrolling
      if (isScrollingRef.current) return;
      
      isScrollingRef.current = true;
      setTimeout(() => {
        isScrollingRef.current = false;
      }, 500); // Increased debounce for smoother navigation

      // Hide scroll indicator after first scroll
      if (!hasScrolled) {
        setHasScrolled(true);
      }

      // Get current state values
      const currentYearData = footprintStory[activeYearIndex];
      const hasMultipleMems = currentYearData.memories.length > 1;

      if (e.deltaY > 0) {
        // Scrolling down - progress forward
        if (hasMultipleMems && activeMemoryIndex < currentYearData.memories.length - 1) {
          // First navigate through memories
          setActiveMemoryIndex(prev => prev + 1);
        } else if (activeYearIndex < footprintStory.length - 1) {
          // Then move to next year
          const nextIndex = activeYearIndex + 1;
          if (timelineRef.current) {
            const itemHeight = 200;
            timelineRef.current.scrollTo({
              top: nextIndex * itemHeight,
              behavior: 'smooth'
            });
          }
          setActiveYearIndex(nextIndex);
          setActiveMemoryIndex(0);
        }
      } else if (e.deltaY < 0) {
        // Scrolling up - go backward
        if (hasMultipleMems && activeMemoryIndex > 0) {
          // First navigate through memories backwards
          setActiveMemoryIndex(prev => prev - 1);
        } else if (activeYearIndex > 0) {
          // Then move to previous year (start at last memory)
          const prevYearIndex = activeYearIndex - 1;
          const prevYear = footprintStory[prevYearIndex];
          if (timelineRef.current) {
            const itemHeight = 200;
            timelineRef.current.scrollTo({
              top: prevYearIndex * itemHeight,
              behavior: 'smooth'
            });
          }
          setActiveYearIndex(prevYearIndex);
          setActiveMemoryIndex(prevYear.memories.length - 1);
        }
      }
    };

    // Add wheel event listener to the entire window
    window.addEventListener('wheel', handleWheel, { passive: false });
    
    return () => {
      window.removeEventListener('wheel', handleWheel);
    };
  }, [activeYearIndex, activeMemoryIndex, footprintStory, hasScrolled]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'ArrowUp') {
        if (hasMultipleMemories && activeMemoryIndex > 0) {
          navigateMemory('prev');
        } else if (activeYearIndex > 0) {
          const prevYearIndex = activeYearIndex - 1;
          const prevYear = footprintStory[prevYearIndex];
          scrollToYear(prevYearIndex);
          setActiveMemoryIndex(prevYear.memories.length - 1);
        }
      } else if (e.key === 'ArrowDown') {
        if (hasMultipleMemories && activeMemoryIndex < currentYear.memories.length - 1) {
          navigateMemory('next');
        } else if (activeYearIndex < footprintStory.length - 1) {
          scrollToYear(activeYearIndex + 1);
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [activeMemoryIndex, activeYearIndex, currentYear.memories.length, hasMultipleMemories, footprintStory, navigateMemory, scrollToYear]);

const [isMobile, setIsMobile] = useState(false);

useEffect(() => {
  const checkMobile = () => {
    setIsMobile(window.innerWidth < 768);
  };

  checkMobile();
  window.addEventListener('resize', checkMobile);
  return () => window.removeEventListener('resize', checkMobile);
}, []);

if (isMobile) {
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <Header />

      <div className="pt-28 px-6 space-y-20">
        {portfolioData.footprintStory.map((year) => (
          <div key={year.year} className="space-y-6">
            <h2 className="text-3xl font-bold text-[#00b4d8]">
              {year.year}
            </h2>

            {year.memories.map((memory, idx) => (
              <div key={idx} className="space-y-4">
                <img
                  src={memory.imageUrl}
                  alt={memory.title}
                  className="w-full h-56 object-cover rounded-xl"
                />

                <h3 className="text-xl font-semibold">
                  {memory.title}
                </h3>

                <p className="text-[#dbdbdb] leading-relaxed">
                  {memory.description}
                </p>
              </div>
            ))}
          </div>
        ))}
      </div>

      <BuiltBy />
    </div>
  );
}

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white overflow-hidden">
      <Header />
      
      {/* Main Fixed Container */}
      <div className="fixed inset-0 top-20 flex">
        {/* Background Image - Changes based on active year */}
        <div className="absolute inset-0 transition-opacity duration-700">
          <img
            src={currentMemory.imageUrl}
            alt={currentMemory.title}
            className="w-full h-full object-cover"
            key={`bg-${activeYearIndex}-${activeMemoryIndex}`}
          />
          {/* Dark overlay */}
          <div className="absolute inset-0 bg-[#0a0a0a]/75"></div>
        </div>

        {/* Content Container */}
        <div className="relative z-10 w-full flex">
          {/* Left Timeline Section (35%) */}
          <div className="w-[35%] flex items-start justify-end pr-16 pt-20">
            <div className="relative h-[80vh]">
              {/* Vertical Line */}
              <div className="absolute right-0 top-0 bottom-0 w-0.5 bg-white/20"></div>

              {/* Scrollable Years Container */}
              <div 
                ref={timelineRef}
                className="h-full overflow-y-auto scrollbar-hide relative pointer-events-none"
                style={{ 
                  scrollSnapType: 'y mandatory',
                  paddingTop: '35vh',
                  paddingBottom: '35vh'
                }}
              >
                {footprintStory.map((yearData, index) => {
                  const isActive = index === activeYearIndex;
                  
                  return (
                    <button
                      key={yearData.year}
                      onClick={() => scrollToYear(index)}
                      className="flex items-center justify-end gap-4 mb-32 relative group transition-all duration-300 pointer-events-auto"
                      style={{ scrollSnapAlign: 'center' }}
                    >
                      {/* Year Label */}
                      <div className={`text-right transition-all duration-300 ${
                        isActive 
                          ? 'text-5xl font-bold text-[#00b4d8]' 
                          : 'text-2xl font-medium text-[#666666] group-hover:text-white'
                      }`}>
                        {yearData.year}
                      </div>

                      {/* Circle on the line */}
                      <div className={`w-4 h-4 rounded-full border-2 transition-all duration-300 relative z-10 ${
                        isActive 
                          ? 'bg-[#00b4d8] border-[#00b4d8] scale-150' 
                          : 'bg-[#0a0a0a] border-white/40 group-hover:border-[#00b4d8] group-hover:bg-[#00b4d8]/20'
                      }`}></div>

                      {/* Memory count indicator */}
                      {yearData.memories.length > 1 && (
                        <div className={`absolute -right-8 text-xs transition-colors ${
                          isActive ? 'text-[#00b4d8]' : 'text-[#666666]'
                        }`}>
                          ({yearData.memories.length})
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Right Content Section (65%) */}
          <div className="w-[65%] flex items-start pl-14 pr-12 pt-20">
            <div className="max-w-3xl">
              {/* Memory Navigation - Only show if multiple memories */}
              {hasMultipleMemories && (
                <div className="mb-8 flex items-center gap-4">
                  <button
                    onClick={() => navigateMemory('prev')}
                    disabled={activeMemoryIndex === 0}
                    className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                      activeMemoryIndex === 0
                        ? 'border-white/20 text-white/20 cursor-not-allowed'
                        : 'border-[#00b4d8] text-[#00b4d8] hover:bg-[#00b4d8] hover:text-white'
                    }`}
                  >
                    <ChevronUp size={20} />
                  </button>

                  <div className="flex gap-2">
                    {currentYear.memories.map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => setActiveMemoryIndex(idx)}
                        className={`transition-all ${
                          idx === activeMemoryIndex
                            ? 'w-8 h-1 bg-[#00b4d8]'
                            : 'w-4 h-1 bg-white/30 hover:bg-white/50'
                        }`}
                      />
                    ))}
                  </div>

                  <button
                    onClick={() => navigateMemory('next')}
                    disabled={activeMemoryIndex === currentYear.memories.length - 1}
                    className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                      activeMemoryIndex === currentYear.memories.length - 1
                        ? 'border-white/20 text-white/20 cursor-not-allowed'
                        : 'border-[#00b4d8] text-[#00b4d8] hover:bg-[#00b4d8] hover:text-white'
                    }`}
                  >
                    <ChevronDown size={20} />
                  </button>

                  <span className="text-sm text-[#666666] ml-2">
                    {activeMemoryIndex + 1} / {currentYear.memories.length}
                  </span>
                </div>
              )}

              {/* Phase Badge */}
              {/* <div className="mb-6">
                <span className="inline-block text-xs font-bold text-[#00b4d8] uppercase tracking-wider bg-[#00b4d8]/10 px-4 py-2 rounded-full border border-[#00b4d8]/30">
                  {currentMemory.phase}
                </span>
              </div> */}

              {/* Title - Animated on change */}
              <h1 
                className="text-3xl md:text-5xl font-bold mb-8 leading-tight transition-opacity duration-500"
                key={`title-${activeYearIndex}-${activeMemoryIndex}`}
              >
                {currentMemory.title}
              </h1>

              {/* Description - Animated on change */}
              <p 
                className="text-l text-[#dbdbdb] leading-relaxed mb-10 whitespace-pre-line transition-opacity duration-500"
                key={`desc-${activeYearIndex}-${activeMemoryIndex}`}
              >
                {currentMemory.description}
              </p>

              {/* Milestone */}
              {/* <div className="border-l-4 border-[#00b4d8] pl-6 py-2">
                <p className="text-sm text-[#666666] uppercase tracking-wider mb-2">Key Milestone</p>
                <p 
                  className="text-lg font-medium text-white transition-opacity duration-500"
                  key={`milestone-${activeYearIndex}-${activeMemoryIndex}`}
                >
                  {currentMemory.milestone}
                </p>
              </div> */}

              {/* Journey message for last year */}
              {/* {activeYearIndex === footprintStory.length - 1 && (
                <div className="mt-12 pt-8 border-t border-white/10">
                  <p className="text-lg text-[#00b4d8] font-medium mb-6">
                    My story of discovery: This where I’ve been, what I’ve built, and who I’ve impacted across data, communiy and management. Now, the journey continues...
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <a
                      href="mailto:alex.chen@example.com"
                      className="bg-[#00b4d8] text-white px-8 py-4 rounded-lg font-semibold hover:bg-[#0096c7] transition-colors text-center"
                    >
                      Start a Conversation
                    </a>
                    <a
                      href="/projects"
                      className="border-2 border-[#00b4d8] text-[#00b4d8] px-8 py-4 rounded-lg font-semibold hover:bg-[#00b4d8] hover:text-white transition-all text-center"
                    >
                      See What I've Built
                    </a>
                  </div>
                </div>
              )} */}
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator - Bottom center, fades after first scroll */}
      <div className={`fixed bottom-8 left-1/2 transform -translate-x-1/2 z-20 flex flex-col items-center gap-2 pointer-events-none transition-opacity duration-500 ${
        hasScrolled ? 'opacity-0' : 'opacity-100 animate-bounce'
      }`}>
        <span className="text-xs text-[#666666] uppercase tracking-wider">Scroll anywhere</span>
        <div className="w-6 h-10 border-2 border-[#666666] rounded-full flex justify-center pt-2">
          <div className="w-1 h-2 bg-[#00b4d8] rounded-full animate-pulse"></div>
        </div>
      </div>

      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
};

export default MyFootprintPage;
